// Creating and editing bookings — plural — with ZT's save flow (round 22).
//
// The form holds ONE job, ONE window, and MANY resources; `existing` maps a
// resource to the booking it already has, so a save reconciles per resource:
// update the kept ones, create the new ones, release the removed ones. The
// dates and times apply to every resource in the list, which is safe
// precisely because the caller chose the set (ZT's comment, kept).
//
// The save is two phases. First every resource is DRY-RUN against the server
// — same locks, same conflict query as a real save, no writes — and every
// clash comes back with its release plan attached. If anything clashed, the
// panel lists it per resource: tick to free the resource from what is in the
// way (Move & Schedule), untick to leave it out entirely. Then the real
// saves run one at a time, carrying each resource's release list; a resource
// whose plan touched days already worked sends `allowPastRewrite` only after
// the panel showed its ⚠ line — the server refuses otherwise, by design.
//
// There is still no override button and there never will be — releasing the
// blocker IS the resolution, named and shown, never silent (spec 05).
//
// Unticking EVERY resource on an edit is allowed, and it means release: the
// primary button relabels itself and routes to the same releaseAll() the
// footer's Release uses (round 37, item 4). Create still requires a resource.
import { useEffect, useMemo, useState } from 'react';
import {
  BOOKABLE_KINDS,
  conflictsFrom,
  scheduling,
  type CalendarBooking,
  type Conflict,
  type JobListItem,
  type Resource,
} from '../schedulingApi';
import { ApiError } from '../api';
import { dateRangeLabel, eachDate, formatClockRange } from '../lib/dates';
import { KIND_LABEL } from '../lib/resourceGroups';
import { nwLabel, type NwLookup } from '../lib/nonWorkingDays';
import { Modal } from './ui/Modal';
import {
  ScheduleConflictPanel,
  isReleasable,
  type ResourceConflicts,
} from './schedule/ScheduleConflictPanel';

interface Props {
  jobs: JobListItem[];
  resources: Resource[];
  /** The bookings being edited — one form, their shared job and window.
      Empty or absent when creating. */
  entries?: CalendarBooking[];
  /** Pre-filled when the manager clicked a day, a row, or came from a job. */
  initial?: { startDate?: string; endDate?: string; jobId?: string; resourceId?: string };
  onClose: () => void;
  /** Everything applied — the caller reloads. */
  onSaved: () => void;
  nwInfo?: NwLookup;
}

const PICKER_KINDS = ['all', 'person', 'plant', 'vehicle', 'subcontractor', 'other'] as const;

export function BookingModal({ jobs, resources, entries, initial, onClose, onSaved, nwInfo }: Props) {
  const editing = (entries?.length ?? 0) > 0;
  const first = entries?.[0];

  const [jobId, setJobId] = useState(first?.jobId ?? initial?.jobId ?? '');
  const [resourceIds, setResourceIds] = useState<string[]>(() => {
    if (entries?.length) return entries.map((e) => e.resourceId);
    return initial?.resourceId ? [initial.resourceId] : [];
  });
  // Which booking each resource already has. Created ids are recorded here
  // as the saves land, so a straggler retry cannot double-book.
  const [existing, setExisting] = useState<Record<string, string>>(() =>
    Object.fromEntries((entries ?? []).map((e) => [e.resourceId, e.id]))
  );

  const [startDate, setStartDate] = useState(
    first?.startDate ?? initial?.startDate ?? initial?.endDate ?? ''
  );
  const [endDate, setEndDate] = useState(
    first?.endDate ?? initial?.endDate ?? initial?.startDate ?? ''
  );
  const [allDay, setAllDay] = useState(first ? first.startTime === null : true);
  const [startTime, setStartTime] = useState(first?.startTime ?? '07:00');
  const [endTime, setEndTime] = useState(first?.endTime ?? '15:30');
  const [note, setNote] = useState<string>(first?.note ?? '');

  const [pickerKind, setPickerKind] = useState<(typeof PICKER_KINDS)[number]>('all');
  const [pickerSearch, setPickerSearch] = useState('');

  const [panel, setPanel] = useState<ResourceConflicts[] | null>(null);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Only available, bookable resources are offered — plus any already on the
  // form, so an edit is not silently rewritten.
  const bookable = useMemo(
    () =>
      resources.filter(
        (r) =>
          (BOOKABLE_KINDS.includes(r.kind) && r.status === 'available') ||
          resourceIds.includes(r.id)
      ),
    [resources, resourceIds]
  );

  const pickerRows = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    return bookable
      .filter((r) => pickerKind === 'all' || r.kind === pickerKind)
      .filter(
        (r) =>
          q === '' ||
          r.name.toLowerCase().includes(q) ||
          (r.identifier ?? '').toLowerCase().includes(q) ||
          (r.registrationNumber ?? '').toLowerCase().includes(q)
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [bookable, pickerKind, pickerSearch]);

  const resourceName = (id: string) => resources.find((r) => r.id === id)?.name ?? 'that resource';

  const toggleResource = (id: string) => {
    setPanel(null);
    setResourceIds((current) =>
      current.includes(id) ? current.filter((r) => r !== id) : [...current, id]
    );
  };

  // Any edit to the window invalidates a standing panel — its plans described
  // a window that no longer exists.
  useEffect(() => {
    setPanel(null);
  }, [jobId, startDate, endDate, allDay, startTime, endTime]);

  function windowBody() {
    return {
      jobId,
      startDate,
      endDate,
      startTime: allDay ? null : startTime,
      endTime: allDay ? null : endTime,
      note: note.trim() === '' ? null : note.trim(),
    };
  }

  function validate(): string | null {
    if (!jobId) return 'Pick a job.';
    // Zero resources is only nonsense on a CREATE. On an edit it means
    // "take everyone off this booking", which is a release — the footer
    // routes that click to releaseAll() before this ever runs (round 37,
    // item 4: unticking the person on leave used to dead-end here, with
    // release the one thing the manager was trying to do).
    if (resourceIds.length === 0 && Object.keys(existing).length === 0) {
      return 'Pick at least one resource.';
    }
    if (!startDate || !endDate) return 'Give the booking its dates.';
    // Only the START date pushed the other one along, so a manager who set the
    // window and then corrected the "To" field backwards got no complaint here
    // and a booking whose last day was before its first — which every board
    // then draws as nothing at all. MaintenanceModal has always refused it; the
    // two forms disagreeing about the same mistake is how it survived.
    if (endDate < startDate) return 'The last day must not be before the first.';
    if (!allDay && (!startTime || !endTime)) return 'Give both times, or tick all day.';
    if (!allDay && startTime >= endTime) return 'The finish time must be after the start.';
    return null;
  }

  /** Phase 1: dry-run every resource, collect every clash. */
  async function check() {
    const invalid = validate();
    if (invalid) {
      setError(invalid);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const found: ResourceConflicts[] = [];
      for (const rid of resourceIds) {
        try {
          const body = { ...windowBody(), resourceId: rid, dryRun: true };
          if (existing[rid]) await scheduling.updateBooking(existing[rid]!, body);
          else await scheduling.createBooking(body);
        } catch (err) {
          const conflicts = conflictsFrom(err);
          if (conflicts) {
            found.push({ resourceId: rid, resourceName: resourceName(rid), conflicts });
          } else {
            throw err;
          }
        }
      }

      if (found.length === 0) {
        await apply(new Set(), []);
        return;
      }
      setPanel(found);
      // Seed every releasable resource ticked — the manager came here to book
      // them, and unticking is the deliberate act (ZT's default).
      setTicked(new Set(found.filter(isReleasable).map((f) => f.resourceId)));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Phase 2: the real saves, one resource at a time so one clash never hides
   * another. `skip` holds clashing resources left out (unticked or
   * unreleasable); their bookings stay exactly as they were.
   */
  async function apply(skip: Set<string>, panelEntries: ResourceConflicts[]) {
    setBusy(true);
    setError(null);
    try {
      const byResource = new Map(panelEntries.map((e) => [e.resourceId, e]));

      for (const rid of resourceIds) {
        if (skip.has(rid)) continue;

        const entry = byResource.get(rid);
        const bookingClashes = (entry?.conflicts ?? []).filter(
          (c): c is Conflict & { type: 'booking' } => c.type === 'booking'
        );
        const release = bookingClashes.map((c) => c.bookingId);
        // Sent only after the panel showed its ⚠ — the server refuses otherwise.
        const allowPastRewrite = bookingClashes.some(
          (c) => c.ifReleased.action !== 'none' && c.ifReleased.rewritesPast
        );

        const body = {
          ...windowBody(),
          resourceId: rid,
          ...(release.length > 0 ? { release, allowPastRewrite } : {}),
        };
        if (existing[rid]) {
          await scheduling.updateBooking(existing[rid]!, body);
        } else {
          const result = await scheduling.createBooking(body);
          // Recorded immediately: if a later resource clashes and the manager
          // saves again, this one updates instead of double-booking.
          setExisting((current) => ({ ...current, [rid]: result.booking.id }));
        }
      }

      // Resources removed from an edit: their bookings are released whole.
      for (const [rid, bookingId] of Object.entries(existing)) {
        if (resourceIds.includes(rid)) continue;
        await scheduling.releaseBooking(bookingId);
      }

      onSaved();
    } catch (err) {
      const conflicts = conflictsFrom(err);
      if (conflicts) {
        // A fresh clash appeared between the check and the save — someone
        // else's booking landed. Send the manager back through the check so
        // the panel tells the current truth, not a stale one.
        setError('Something changed while you were looking. Check the clashes again.');
        setPanel(null);
      } else {
        setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  function confirmFromPanel() {
    if (!panel) return;
    const skip = new Set(
      panel
        .filter((entry) => !ticked.has(entry.resourceId) || !isReleasable(entry))
        .map((entry) => entry.resourceId)
    );
    void apply(skip, panel);
  }

  async function releaseAll() {
    const ids = Object.values(existing);
    if (ids.length === 0) return;
    const label = ids.length === 1 ? 'this booking' : `all ${ids.length} bookings`;
    if (!window.confirm(`Release ${label}?`)) return;
    setBusy(true);
    setError(null);
    try {
      let truncated: string | null = null;
      for (const id of ids) {
        const result = await scheduling.releaseBooking(id);
        if (result.outcome === 'truncated') truncated = result.newEndDate ?? null;
      }
      if (truncated) {
        window.alert(
          `Bookings already under way keep their worked days on the record; ` +
            `the last one now ends on ${truncated}.`
        );
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not release.');
    } finally {
      setBusy(false);
    }
  }

  const clashedCount = panel?.length ?? 0;
  const movedCount = panel
    ? panel.filter((e) => ticked.has(e.resourceId) && isReleasable(e)).length
    : 0;
  const confirmLabel =
    movedCount === 0
      ? clashedCount === resourceIds.length
        ? 'Nothing to schedule'
        : 'Schedule the rest'
      : movedCount === 1
        ? 'Move & schedule'
        : `Move ${movedCount} & schedule`;

  const title = editing
    ? (entries?.length ?? 0) > 1
      ? `Edit ${entries!.length} resources`
      : 'Edit booking'
    : 'Book resources';

  // Every resource unticked on an edit: the save IS a release, and it goes
  // through releaseAll() — the same confirm, truncation notice and error
  // handling as the footer's own Release button, so the two paths cannot
  // disagree about what releasing means.
  const releasingInstead = editing && resourceIds.length === 0;
  const releaseCount = Object.keys(existing).length;

  return (
    <Modal
      title={title}
      onClose={onClose}
      size="xl"
      footer={
        <>
          {editing && (
            <button
              type="button"
              className="row-action-danger mr-auto"
              onClick={() => void releaseAll()}
              disabled={busy}
            >
              {(entries?.length ?? 0) > 1 ? `Release ${entries!.length} bookings` : 'Release'}
            </button>
          )}
          <button type="button" onClick={onClose} className="btn-ghost btn-sm" disabled={busy}>
            Cancel
          </button>
          {!panel && (
            <button
              type="button"
              onClick={() => void (releasingInstead ? releaseAll() : check())}
              disabled={busy}
              className="btn-primary btn-sm"
            >
              {busy
                ? 'Working…'
                : releasingInstead
                  ? releaseCount > 1
                    ? `Release ${releaseCount} bookings`
                    : 'Release booking'
                  : editing
                    ? 'Save changes'
                    : 'Book it'}
            </button>
          )}
        </>
      }
    >
      {error && (
        <div className="banner-error mb-3" role="alert">
          {error}
        </div>
      )}

      {panel && (
        <ScheduleConflictPanel
          entries={panel}
          ticked={ticked}
          onToggle={(rid) =>
            setTicked((current) => {
              const next = new Set(current);
              if (next.has(rid)) next.delete(rid);
              else next.add(rid);
              return next;
            })
          }
          confirmLabel={confirmLabel}
          onConfirm={confirmFromPanel}
          busy={busy}
        />
      )}

      <form onSubmit={(e) => e.preventDefault()} noValidate className="space-y-4">
        <div>
          <label className="form-label" htmlFor="jobId">
            Job
          </label>
          <select
            id="jobId"
            className="form-select"
            value={jobId}
            onChange={(e) => setJobId(e.target.value)}
            required
          >
            <option value="">Choose a job…</option>
            {jobs
              .filter((j) => j.status !== 'cancelled')
              .map((job) => (
                <option key={job.id} value={job.id}>
                  {job.number} · {job.title}
                </option>
              ))}
          </select>
        </div>

        <div>
          <span className="form-label">Resources</span>
          {resourceIds.length > 0 && (
            <p className="mb-1.5 text-xs text-gray-600">
              {resourceIds.map((id) => resourceName(id)).join(', ')}
            </p>
          )}
          <div className="mb-1.5 flex flex-wrap gap-1.5">
            {PICKER_KINDS.map((kind) => (
              <button
                key={kind}
                type="button"
                onClick={() => setPickerKind(kind)}
                aria-pressed={pickerKind === kind}
                className={`min-h-[44px] rounded-full px-3 py-1.5 text-xs font-medium capitalize transition-colors focus:outline-none focus:ring-2 focus:ring-brand-600 sm:min-h-[32px] ${
                  pickerKind === kind
                    ? 'bg-brand-500 text-ink'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {kind === 'all' ? 'All' : KIND_LABEL[kind]}
              </button>
            ))}
          </div>
          <input
            type="search"
            className="form-input mb-1.5"
            placeholder="Search by name, identifier or rego…"
            value={pickerSearch}
            onChange={(e) => setPickerSearch(e.target.value)}
            aria-label="Search resources"
          />
          <ul className="max-h-48 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
            {pickerRows.map((resource) => {
              const on = resourceIds.includes(resource.id);
              return (
                <li key={resource.id}>
                  <label className="flex min-h-[44px] cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 sm:min-h-0">
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggleResource(resource.id)}
                      className="h-5 w-5 flex-none rounded border-gray-300 text-brand-600 focus:ring-brand-500 sm:h-4 sm:w-4"
                    />
                    <span className="min-w-0 flex-1 truncate text-gray-900">
                      {resource.label ?? resource.name}
                      <span className="ml-1.5 text-xs text-gray-500">
                        {resource.category ?? KIND_LABEL[resource.kind]}
                      </span>
                    </span>
                  </label>
                </li>
              );
            })}
            {pickerRows.length === 0 && (
              <li className="px-3 py-3 text-center text-sm text-gray-500">
                Nothing matches that.
              </li>
            )}
          </ul>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="form-label" htmlFor="startDate">
              From
            </label>
            <input
              id="startDate"
              type="date"
              className="form-input"
              value={startDate}
              onChange={(e) => {
                const value = e.target.value;
                setStartDate(value);
                if (endDate === '' || endDate < value) setEndDate(value);
              }}
              required
            />
          </div>
          <div>
            <label className="form-label" htmlFor="endDate">
              To
            </label>
            <input
              id="endDate"
              type="date"
              className="form-input"
              min={startDate || undefined}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              required
            />
          </div>
        </div>

        {startDate !== '' && nwInfo?.(startDate) && (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-red-700">
            Heads up: the start date is a non-working day: {nwLabel(nwInfo(startDate)!)}. You can
            still schedule it.
          </p>
        )}

        <label className="flex min-h-[44px] items-center gap-2 text-sm text-gray-900 sm:min-h-0">
          <input
            type="checkbox"
            className="h-5 w-5 rounded border-gray-300 text-brand-600 focus:ring-brand-500 sm:h-4 sm:w-4"
            checked={allDay}
            onChange={(e) => setAllDay(e.target.checked)}
          />
          All day
        </label>

        {!allDay && (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="form-label" htmlFor="startTime">
                  Start
                </label>
                <input
                  id="startTime"
                  type="time"
                  className="form-input"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="form-label" htmlFor="endTime">
                  Finish
                </label>
                <input
                  id="endTime"
                  type="time"
                  className="form-input"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  required
                />
              </div>
            </div>
            {startDate !== endDate && startDate !== '' && endDate !== '' && (
              <p className="form-hint">
                These times apply to each day from {dateRangeLabel(startDate, endDate)}:{' '}
                {eachDate(startDate, endDate).length} days of{' '}
                {formatClockRange(startTime, endTime)}, not one continuous run.
              </p>
            )}
          </>
        )}

        <div>
          <label className="form-label" htmlFor="booking-note">
            Note
          </label>
          <input
            id="booking-note"
            type="text"
            className="form-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <p className="form-hint">Shown on the booking in the calendar.</p>
        </div>
      </form>
    </Modal>
  );
}
